import type {
  ActivityDto,
  MaterialDto,
  MaterialImpactCalculationDto,
  ProductDto
} from '@/stores/buildingBlock'
import {
  type LifecycleGroupFieldKey,
  LIFECYCLE_COMPARISON_FIELDS,
  type ResultPhaseKey,
  isLifecycleGroupFieldKey,
  PRODUCTION_COMBINED_STAGE,
  PRODUCTION_INDIVIDUAL_STAGES
} from '@/constants/lifecyclePhases'

export const comparisonFields = LIFECYCLE_COMPARISON_FIELDS

export const isGroupColumn = isLifecycleGroupFieldKey

export const formatMaterialImpactFromCalculation = (
  impactCalculation: MaterialImpactCalculationDto | undefined
): string => {
  if (!impactCalculation) return 'NaN'

  const { base_total, conversion_factor, lifetime_factor, total } = impactCalculation

  if (lifetime_factor <= 1) {
    return ` ${base_total.toFixed(2)}(total) x ${conversion_factor.toFixed(2)} (conversion) = € ${total.toFixed(2)}`
  }

  const factorLabel = Number.isInteger(lifetime_factor)
    ? String(lifetime_factor)
    : lifetime_factor.toFixed(2)

  return ` ${base_total.toFixed(2)}(total) x ${conversion_factor.toFixed(2)} (conversion) x ${factorLabel} (lifetime) = € ${total.toFixed(2)}`
}

export const isDifferentField = (
  currentProduct: ProductDto | null,
  material: MaterialDto,
  field: keyof ProductDto | keyof MaterialDto
) => {
  const defaultValue = getComparisonDisplayValue(currentProduct, field as string)
  const newValue = getComparisonDisplayValue(material, field as string)

  if (defaultValue == null && newValue == null) return false

  const defaultNumber = Number(defaultValue)
  const newNumber = Number(newValue)
  const bothNumeric = Number.isFinite(defaultNumber) && Number.isFinite(newNumber)

  if (bothNumeric) {
    // Match UI precision (table renders with 5 decimals) and avoid false positives
    // from float precision or number/string representation differences.
    return Math.abs(defaultNumber - newNumber) > 1e-5
  }

  return defaultValue !== newValue
}

export const getActivitiesForMaterial = (activities: ActivityDto[], materialId: number) => {
  return activities.filter((activity) => activity.material === materialId)
}

export const getSameStageCount = (activities: ActivityDto[], stage: string | null) => {
  if (!stage) return 0
  return activities.filter((activity) => activity.stage === stage).length
}

export const getIsFirstActivityForStage = (
  activities: ActivityDto[],
  activityId: number,
  stage: string | null
) => {
  if (!stage) return true
  const sameStage = activities.filter((activity) => activity.stage === stage)
  if (!sameStage.length) return true
  return sameStage[0].id === activityId
}

export const getComparisonDisplayValue = (
  row: ProductDto | MaterialDto | null | undefined,
  fieldKey: string
): number | null => {
  if (!row) return null

  if (fieldKey === PRODUCTION_COMBINED_STAGE) {
    const individualSum = PRODUCTION_INDIVIDUAL_STAGES.reduce(
      (sum, key) => sum + Number(row[key as keyof typeof row] ?? 0),
      0
    )
    if (individualSum !== 0) return 0
  }

  const raw = row[fieldKey as keyof typeof row]
  return raw == null ? null : Number(raw)
}
